import { GAME_LAYOUT, Settings } from "./settings";
import {
  WILD_SYMBOL_ID,
  SCATTER_SYMBOL_IDS,
  BONUS_SYMBOL_IDS,
} from "./SymbolData";

// Dynamically import provider-specific configuration based on RGS_PROVIDER setting
// Uses require.context pattern to avoid hardcoding provider names
const providerConfigContext = require.context(
  "./RGS/Providers/",
  true,
  /^\.\/[^/]+\/providerConfig\.js$/,
);

// Build provider configs map
const providerConfigs = {};
providerConfigContext.keys().forEach((fileName) => {
  const match = fileName.match(/\.\/(.+)\/providerConfig\.js$/);
  if (match) {
    const folderName = match[1];
    providerConfigs[folderName] =
      providerConfigContext(fileName).PROVIDER_CONFIG;
  }
});

// Select the active provider config
const PROVIDER_CONFIG = providerConfigs[Settings.APP.RGS_PROVIDER];

if (!PROVIDER_CONFIG) {
  throw new Error(
    `Provider config not found for: ${Settings.APP.RGS_PROVIDER}. Available providers: ${Object.keys(providerConfigs).join(", ")}`,
  );
}

// ============================================================================
// REEL DELAY GENERATORS — dynamically create per-reel delay arrays from REEL_COUNT
// ============================================================================

/**
 * Generate a delay array with constant gaps between reels.
 * e.g. generateLinearDelays(3, 375) → [0, 375, 750]
 */
const generateLinearDelays = (count, gap) =>
  Array.from({ length: count }, (_, i) => i * gap);

/**
 * Generate a delay array with gaps that decrease per reel (natural cascade feel).
 * Negative gapReduction means gaps INCREASE per reel.
 * e.g. generateDecreasingDelays(5, 50, 7, 25) → [0, 50, 93, 129, 158]
 */
const generateDecreasingDelays = (
  count,
  initialGap,
  gapReduction = 0,
  minGap = 0,
) => {
  const delays = [0];
  let gap = initialGap;
  for (let i = 1; i < count; i++) {
    delays.push(delays[i - 1] + gap);
    gap = Math.max(gap - gapReduction, minGap);
  }
  return delays;
};

// Feature configuration
export const FEATURES_CONFIG = {
  DEV_TOOLS: {
    DEBUG_BORDER: false, // If true: shows 1px yellow debug border on key components (DimReelSet, etc.) for measuring alignment
    DEBUG_CROSSHAIR: false, // If true: draws 1px red crosshair lines across the full screen at the vertical and horizontal center
    DEV_PANEL: false, // If true: renders a dev overlay panel in the top-left corner with test trigger buttons (shake, transition, etc.)
  },
  BACKGROUND: {
    QUADRANT_SPRITES: false, // If true: renders 4 static background quadrant images (base/feature). If false: spine-only background
    FULLSCREEN_CLICK_SPRITE: true, // If true: renders a full-screen invisible sprite for background click detection (fullscreen toggle, etc.)
    REELSET_BONE_INJECTION: false, // If true: reelset is injected into background spine bones (base_reel/feature_reel) and counter-scaled. If false: traditional centered reelset
  },
  /**
   * GameView — unified spine scene renderer.
   * When ENABLED, replaces BOTH BackgroundAnimation AND GameContainer in Stage.js.
   * The game_view spine handles: background, reel frame, masks, dividers, jackpots.
   * ReelSet, OverlayReelSet, DimReelSet are injected into spine bone slots.
   * When DISABLED, the old BackgroundAnimation + GameContainer architecture is used unchanged.
   */
  GAME_VIEW: {
    ENABLED: true,
  },
  /**
   * Intro screen — controls whether the intro_screen spine is loaded and used.
   * When false, IntroPopup renders a static black screen + "Tap to start" text.
   * Secondary theme (Zeus) does not have a game-specific intro spine yet.
   */
  INTRO_SCREEN: {
    SPINE_ENABLED: false, // If true: intro_screen spine loaded and played. If false: static fallback used.
  },
  /**
   * SlotRegistry — centralized PIXI container injection into spine bone slots.
   * Master toggle gates the entire system; sub-toggles control individual injection points.
   * When ENABLED is false, zero registration/subscription overhead — as if the context doesn't exist.
   */
  SLOT_REGISTRY: {
    ENABLED: true, // Master kill switch — when false, entire SlotRegistry is no-op
    REELSET_INJECTION: false, // Controls base_reel + feature_reel bone injection registration
    LOGO_INJECTION: false, // Controls all 4 logo bone slot registrations
    JACKPOTS_INJECTION: false, // Controls jackpots_position slot injection registration
    RESPIN_COUNTER_INJECTION: false, // Controls bonus_counter slot injection registration
    GAME_VIEW_INJECTION: true, // Controls GameView attachment_slot_placeholder registrations
  },
  CHARACTER_ANIMATION: {
    ENABLED: false, // If true: character spine is loaded, created, and animated alongside the background
  },
  SPINE_ANIMATIONS: {
    LANDING_ENABLED: true, // Enable landing animations
    IDLE_ENABLED: true, // Enable idle animations
    TURBO_LANDING_ENABLED: false, // If false: turbo mode skips landing/idle animations entirely (shows static textures, zero spine CPU cost)
    SHARED_SPINE_SKINS: true, // If true: symbols with a 'skin' field in SymbolData apply skin to shared spine instance (e.g., all LP symbols share one spine file with different skins)
    ROUND_LEVEL_IDLE: true, // If true: landing animations hold on last frame; idle triggers after ALL reels stop (round-level). If false: per-reel landing→idle chain (current behavior)
  },
  DIVIDERS: {
    ENABLED: false, // If false: reel dividers are not rendered or loaded (some games don't use them)
  },
  FREE_SPINS: {
    ENABLED: false,
    ROUNDS: 15, // Number of free spins awarded per trigger
  },
  FREE_ROUNDS: {
    ENABLED: true, // If false: free rounds feature is completely disabled
  },
  TENSION: {
    ENABLED: true, // Master switch — enables the entire tension system (animations, spines, sounds, reel deceleration)
    SYMBOL_COUNTING_ENABLED: false, // Frontend symbol-counting trigger — counts coins/scatters on landed reels to start tension. Disabled when API-driven anticipation is the sole trigger source.
    REELSET_ANIMATION: false, // If true: plays reelset animation (e.g. zoom) during tension
    ENVIRONMENT_ZOOM: false, // If true: background and scene transition containers zoom in sync with reelset
    BACKGROUND_ANIMATION: false, // If true: plays background spine animation during tension
    TRIGGER_SYMBOL: BONUS_SYMBOL_IDS, // Array of symbol IDs that trigger tension — coins (11-25), jackpots (27-29), royal (26)
    SCATTER_SYMBOL: SCATTER_SYMBOL_IDS, // Array of scatter symbol IDs that trigger free spin tension
    MIN_SYMBOLS: 2, // Minimum trigger symbols found to trigger anticipation mode (anticipate the 3rd scatter)
    CUTOFF_THRESHOLD: 3, // Stop tension mode when this many trigger symbols are reached (triggers feature round)
    DURATION: 2667, // Fallback duration (ms) — overridden at runtime by spine's actual tension_in + tension_idle + tension_out durations
    IDLE_LOOPS: 0.4, // ~1000ms per reel (in + idle×0.4 + out ≈ 233+533+233 = 999ms)
    REEL_DECELERATION: {
      ENABLED: true, // If true: tension reels gradually decelerate from full speed to TARGET_SPEED (GDD §10.3)
      DECELERATION_RATIO: 1.0, // Fraction of tension cycle to spend decelerating (1.0 = decelerate through entire cycle)
      TARGET_SPEED: 0.5, // Final spin speed as fraction of normal (0.25 = 25% = 4× slower)
      // Landing velocity is mathematically matched to the decelerated spin speed.
      // TENSION_LANDING_SLOWDOWN compensates for the back-out overshoot feeling faster against the slow spin.
      TENSION_LANDING_SLOWDOWN: 1.0, // Multiplier on landing duration (1.0 = exact velocity match, >1.0 = softer landing)
    },
    // API-driven anticipation (GDD §10.2) — forces ALL 5 reels into tension when RGS flags the spin.
    // Cascade timing is reactive: each reel starts the next via onReelLanded.
    ANTICIPATION: {
      ENABLED: true, // Master toggle — enables anticipation system
      FORCE: false, // Dev toggle — forces anticipation on every spin (set true during development)
    },
  },

  WIN_LINES: {
    ENABLED: false,
  },
  WIN_BOX: {
    ENABLED: true,
    WINNING_ONLY: true, // true = boxes only on winning symbol positions, false = full line path
    SNAP_TO_LOOP: true, // true = win boxes play exact loop count matching display duration (no partial loops)
  },
  WIN_LINE_DIGIT: {
    ENABLED: true,
  },
  // Win animation timing configuration
  // ⚠️  CRITICAL INVARIANT: The master duration comes from the highest-priority symbol's
  //     actual spine "win" animation (e.g. w0/wild = 2000ms). All other symbols' loops
  //     are integer multiples of this master (LP/MP 1000ms × 2 = 2000ms).
  //     DO NOT scale or halve the master — it breaks frame-0 alignment on loop boundaries.
  WIN_ANIMATION_TIMING: {
    SPINE_LOOP_DURATION: 1000, // Fallback only — used when spine instance not yet available. Real duration comes from the spine data at runtime.
    DURATION_SCALE: 1.0, // ⚠️  MUST be 1.0. Multiplier on the master symbol's win animation. Changing this cuts the loop short (e.g. 0.5 = wild plays half its animation).
    SPIN_UNBLOCK_DELAY: 1000, // Fixed delay (ms) before spin button unblocks after a win. Independent of animation duration.
    WIN_LINES_DELAY: 250, // Delay before showing win animations after spin completes (in ms)
    FREE_SPIN_WIN_LINES_DELAY: 400, // Delay before showing win animations in free spins (in ms)
    LOOPS_PER_LINE: 1, // Number of master-duration loops per winning line during line cycling
    get LINE_CYCLE_DURATION() {
      return this.SPINE_LOOP_DURATION; // Fallback duration for line cycling when spine instance not available
    },
    // Fast win animation for turbo/autoplay — shorter loop for snappier feel.
    // Falls back to regular "win" if the animation doesn't exist in the spine.
    WIN_FAST: {
      ENABLED: true, // If true: turbo/autoplay uses "win_fast" animation when available
      ANIMATION_NAME: "win_fast", // Spine animation name to look for (must be exact match)
      WIN_TEXT_SPEED_UP: true, // If true: auto-scales WinText spine to fit in→idle→out into fast win duration
    },
  },
  SHOW_ALL_WINS: {
    ENABLED: true, // If true: shows all winning symbols at once with global win text before line cycling
    LOOPS_FOR_SHOW_ALL: 1, // Number of animation loops for "show all wins" phase (event-based timing)
    get DISPLAY_DURATION() {
      return FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPINE_LOOP_DURATION; // 1500ms - matches spine loop (fallback for timeout)
    },
    EXCLUDE_SCATTER: true, // If true: scatter wins skip "show all wins" and go directly to line cycling
  },
  EXPANDING_WILDS: {
    ENABLED: false, // If false: expanding wild feature is completely disabled, win lines start immediately after spin
    MULTIPLIER_ENABLED: false, // If false: expanding wild multiplier feature is disabled (no multiplier sounds or visuals)
    ANIMATION_MODE: "cascade", // "cascade" | "full_reel" | "instant" - cascade: symbol-by-symbol, full_reel: spine overlay, instant: no animation
    SHOW_ALL_WINS_BEFORE_CYCLING: true, // If true: shows all winning symbols after expanding wild animation (overrides SHOW_ALL_WINS.ENABLED)
    // If false: uses SHOW_ALL_WINS.ENABLED setting to determine behavior
    LOOPS_FOR_EXPANDING_WILD_SHOW_ALL: 1, // Number of animation loops for "show all wins" after expanding wild (event-based timing)
  },
  TURBO_SUGGESTION: {
    ENABLED: Settings.APP.UI_THEME === "primary", // Only primary theme has turbo suggestion popup
  },
  RESPONSIBLE_GAMBLING: {
    ENABLED: Settings.APP.UI_THEME === "secondary", // Enabled for secondary theme, disabled for primary
  },
  HEARTBEAT: {
    ENABLED: false, // If false: heartbeat manager disabled, no periodic balance sync
    INTERVAL: 30000, // Heartbeat interval in milliseconds (30 seconds)
    START_DELAY: 30000, // Delay before first heartbeat (30 seconds)
    PAUSE_DURING_SPIN: true, // Pause heartbeat during active spins
  },
  MINI_PAYTABLE: {
    ENABLED: false, // If false: mini paytable (symbol info popup on long-press) is completely disabled
  },
  BONUS_BUY: {
    ENABLED: true, // If false: bonus buy button and popup completely disabled
  },
  POT_CHARGING: {
    ENABLED: true, // If true: lightning trails fire from Zeus symbols to matching POT bones on reel landing
    TRAIL_DURATION: 160, // Travel time for lightning trail (ms) — 120 charge + 160 travel + 120 impact = 400ms total
  },
  SUPER_TURBO: {
    ENABLED: true, // If true: super-turbo speed mode available (base game only, skips regular win animations)
  },

  POT_COLLECTION: {
    ENABLED: true, // Pot Collection (Zeus Respins) — active H&W variant for this game
    MAX_WIN_MULTIPLIER: PROVIDER_CONFIG.MAX_WIN_MULTIPLIER, // GDD §18.2 — sourced from providerConfig
    INTRO_DELAY: 500, // Delay before entrance popup after reels stop (ms)
    ENTRANCE_POPUP_MIN_DISPLAY: 3000, // GDD §16.4: 3s minimum before button appears
    MAX_COINS: 15, // GDD: 5 reels × 3 rows = 15 coin positions
    INITIAL_RESPINS: 3, // GDD §5.5: always 3 respins at start
    GRAND_JACKPOT_MULTIPLIER: 5000, // GDD §5.6: 5000× on grid fill
    GRAND_JACKPOT_POPUP_DELAY: 1000, // Delay before showing GJP popup after grid fill
    TOTAL_WIN_AUTO_CLOSE: 30000, // GDD §19: auto-close after 30s
    // Autoplay — inter-spin delay is a flat 180ms constant in PotCollectionControlContext
    // Symbol tension mode — per-symbol anticipation for last blank cell(s)
    // Universal pattern: any hold-and-win game can use this config shape.
    SYMBOL_TENSION: {
      ENABLED: true, // Enable per-symbol tension/anticipation
      DURATION: 4233, // How long the symbol spins in tension before stopping (ms) — 233ms IN + 4000ms LOOP to match sound
      TENSION_START_DELAY: 100, // Delay after last non-tension cell stops before starting tension (ms)
    },
    // Collection animation
    COLLECTION: {
      VALUE_TRAVEL_DURATION: 500, // Duration for value text traveling to Win Counter (ms)
      ROYAL_COLLECTION_DELAY: 500, // Delay before end-of-bonus collection starts
    },
    COLLECTOR_FONT_SIZE_MULTIPLIER: 0.15, // Collector counter text size as fraction of spine nativeHeight
    // Spin speed config — bonus always runs at AUTO speed
    SPIN_SPEED: {
      SPEED_MS_PER_SYMBOL_HEIGHT: 126, // Slightly slower than base (120ms)
      STOP_SLOWDOWN_FACTOR: 1.0, // Landing speed vs spin speed (1.0 = exact velocity match)
      TENSION_LANDING_SLOWDOWN: 1.0, // No extra slowdown — landing matches decelerated velocity
      ACCEL_SPEED_RATIO: 1.25, // Acceleration duration = SPEED_MS × ratio (160 × 1.25 = 200ms)
      BOUNCE_OVERSHOOT: 1.75,
      BOUNCE_OVERSHOOT_MANUAL: 0.8, // Snappier landing on manual stop (less bounce)
      STOP_SLOWDOWN_FACTOR_MANUAL: 0.75,
      // N=1 stop travel can collapse to ~0 after drift — floor distance (not duration)
      // so slam-stop always drops in from above. Base game uses N=3 so rarely needs this.
      MIN_STOP_DISTANCE_FRACTION_MANUAL: 0.75,
      SPIN_DURATION: 400, // Spin loop duration before auto-stop (ms) — was 500
      SYMBOL_STOP_BASE_DELAY: 50,
      SYMBOL_STOP_CASCADE_DELAY: 200, // Target ~3500ms round: 500 + 50 + 200×14 + ~200 ≈ 3550ms
      SYMBOL_STOP_CASCADE_DELAY_MANUAL: 0, // All cells stop simultaneously on manual stop
      COIN_TRAVEL_DURATION: null,
      STAGGER_RATIO: 1.0,
      WIND_UP_ENABLED: true,
      WIND_UP_CASCADE_DELAY: 20,
      WIND_UP_SPEED_FACTOR: 0.4, // Multiplier on wind-up duration (<1.0 = snappier)
      WIND_UP_DISTANCE: 0.05,
    },
    // Feature animation speed overrides — controls default speed mode per feature.
    // When SLAM_STOP_BUTTON_ENABLED is false, player cannot skip features via spacebar/button.
    // Each *_ALWAYS_FAST flag forces that feature to run at slam-stop speed by default.
    // To restore original slam-stop behavior: set SLAM_STOP_BUTTON_ENABLED: true, all *_ALWAYS_FAST: false.
    FEATURE_SPEED: {
      SLAM_STOP_BUTTON_ENABLED: false, // Allow player to slam-stop features via spacebar/button
      ENTRY_SPAWN_TURBO: false,        // Entry sequence: coins spawn 1-by-1 (player can slam-stop to speed up)
      MYSTERY_ALWAYS_FAST: false,      // Mystery shuffle — false = always play full animation
      DOUBLE_ALWAYS_FAST: true,        // Double trails — true = all fire at once
      COLLECT_ALWAYS_FAST: true,       // Collect trails — true = all fire at once
    },
  },

  ROYAL_COLLECT: {
    ENABLED: false, // If true: Royal Collection feature is active (separate from Hold and Win)
    // Shared config (not speed-dependent)
    BLURRED_TEXTURES_ENABLED: true, // Enable blur crossfade during RC spins
    BLUR_VELOCITY_THRESHOLD: 0.6, // (inactive when BLURRED_TEXTURES_ENABLED is false)
    STOP_SLOWDOWN_FACTOR: 1.0, // Seamless transition from spin to stop
    // Autoplay
    AUTOPLAY_FIRST_SPIN_DELAY: 800, // Delay after transition before first spin (ms)
    AUTOPLAY_SPIN_DELAY: 550, // Delay between continuous spins (ms) — non-coin rounds only
    // Collection animation — coin/jackpot travel to royal
    COLLECTION: {
      COIN_TRAVEL_DURATION: 500, // Canonical travel duration for ALL collectibles (ms) — matches c.json collect animation (500ms)
      ROYAL_COLLECTION_DELAY: 500, // Delay before royal collection starts after last coin collection (ms)
      ROYAL_ARC_LATERAL_OFFSET: 1.0, // Lateral offset for royal arc as fraction of symbolWidth (wider = more circular)
      ROYAL_DESTINATION_Y_OFFSET: -0.8, // Y offset as fraction of symbolHeight above row 0 center
    },
    // Speed-dependent config — mirrors base game's AUTO_PLAY_SPEEDS pattern
    AUTO_PLAY_SPEEDS: {
      AUTO: {
        SPEED_MS_PER_SYMBOL_HEIGHT: 120, // Deliberate bonus spin feel (slower than main game)
        ACCELERATION_DURATION: 200, // Ease-out cubic ramp from 0→1 (ms)
        BOUNCE_OVERSHOOT: 1.1, // Landing bounce — matches base game AUTO
        LANDING_DURATION: 375, // Fixed landing animation duration (ms)
        SPIN_DURATION: 500, // Auto-stop delay after spin start (ms)
        SYMBOL_STOP_BASE_DELAY: 50, // Base delay before first cascading stop (ms)
        SYMBOL_STOP_CASCADE_DELAY: 400, // Delay between each symbol stop (ms)
        SYMBOL_STOP_CASCADE_DELAY_MANUAL: 75, // Manual stop cascade — fast but still visible one-by-one
        COIN_TRAVEL_DURATION: null, // null = use spine's collect animation natural duration
        STAGGER_RATIO: 1.0, // Sequential: each coin/royal completes full arc before next starts
        // Wind-up animation (AUTO speed only — QUICK and TURBO skip it)
        WIND_UP_ENABLED: true, // Enable per-symbol wind-up before spin starts
        WIND_UP_CASCADE_DELAY: 30, // ms between each symbol starting its wind-up (cascade effect)
        WIND_UP_DISTANCE: 0.15, // Wind-up pull distance as fraction of symbolHeight (tune for feel)
      },
      QUICK: {
        SPEED_MS_PER_SYMBOL_HEIGHT: 75, // A bit faster than AUTO (120ms)
        ACCELERATION_DURATION: 200, // Smooth ramp-up (matches base game)
        BOUNCE_OVERSHOOT: 1.0, // Landing bounce — matches base game QUICK
        LANDING_DURATION: 375, // Natural landing (matches base game)
        SPIN_DURATION: 175, // Spin loop before auto-stop (total perceived time ~630ms with cascade)
        SYMBOL_STOP_BASE_DELAY: 0, // No base delay
        SYMBOL_STOP_CASCADE_DELAY: 65, // Tight cascade — rapid-fire stops
        SYMBOL_STOP_CASCADE_DELAY_MANUAL: 25, // Near-instant manual stop
        COIN_TRAVEL_DURATION: 475, // Slightly faster than AUTO (565ms)
        STAGGER_RATIO: 0.5, // Next coin starts when current is 50% through its arc
      },
      TURBO: {
        SPEED_MS_PER_SYMBOL_HEIGHT: 66, // Slower spin, ~3 symbol heights in 210ms
        ACCELERATION_DURATION: 0, // Instant full speed
        BOUNCE_OVERSHOOT: 1.5, // Pronounced landing bounce
        LANDING_DURATION: 200, // Fast snap landing
        SPIN_DURATION: 210, // ~4 symbol heights of travel before stop
        SYMBOL_STOP_BASE_DELAY: 0, // No base delay
        SYMBOL_STOP_CASCADE_DELAY: 0, // All stop simultaneously
        SYMBOL_STOP_CASCADE_DELAY_MANUAL: 0, // Instant manual stop
        COIN_TRAVEL_DURATION: 435, // Fast travel with scaled animation (ms)
        STAGGER_RATIO: 0, // All coins launch simultaneously (no stagger)
      },
    },
  },
  JACKPOTS: {
    ENABLED: true, // Jackpot values displayed in GameView spine slots
  },
  TOOLBAR: {
    TOP: {
      BACKGROUND_MODE: "back", // "front" = toolbar renders its own bg (on top of game), "back" = bg behind game (effects bleed over)
      TOOLBAR_COLOR: "color", // "color" = solid background, "transparent" = no background
    },
    BOTTOM: {
      BACKGROUND_MODE: "front", // "front" = toolbar renders its own bg (on top of game), "back" = bg behind game (effects bleed over)
      TOOLBAR_COLOR: "color", // "color" = solid background, "transparent" = no background
    },
    REELSET_WIN_DISPLAY: {
      BACKGROUND_MODE: "front", // "front" = win display bar renders on top of reelset, "back" = behind reelset
      TOOLBAR_COLOR: "color", // "color" = solid background with rounded corners, "transparent" = no background
    },
  },
  IOS_FULLSCREEN_OVERLAY: {
    ENABLED: true, // If false: iOS Safari fullscreen swipe overlay is completely disabled
    DEV_MODE: false, // If true: bypasses iOS/Safari detection — shows overlay in any browser for testing (Chrome DevTools etc.)
  },
  LANGUAGE_SWITCH: {
    ENABLED: false, // If false: language selector in settings is hidden (no localization support yet)
  },
  TURBO: {
    SPIN_SENSITIVITY: 400, // Time to hold button before turbo mode activates (ms)
  },
  SPIN: {
    HIDE_IDLE_BUFFERS: true, // If true: buffer sprites (above/below visible area) are hidden when reels are idle to prevent overflow bleed
    COIN_VALUES_ON_REELS: false, // If true: create BitmapText for coin value display on reel sprites (set false when coins only appear in bonus modes)
    BLURRED_TEXTURES_ENABLED: false, // If false: no blur crossfade during spin (use when blurred textures/animations are unavailable)
    // Programmatic blurred texture generation (replaces manual PS export + spritesheet)
    BLURRED_TEXTURE_GENERATION: {
      ENABLED: true, // If true: generate blurred textures from spine at load time. If false: use traditional blurred spritesheet
      SCALE_X: 1.0, // Width scale (1.0 = no change)
      SCALE_Y: 1.0, // Height scale (1.0 = no vertical shrinking)
      BLUR_STRENGTH: 26, // Vertical blur intensity (approximates PS motion blur 90°/30px — higher = more blur)
    },
    // Wind-up animation settings
    WIND_UP_DURATION: 55, // Duration of wind-up animation per reel (ms) - set to 0 to disable wind-up. Fallback when velocity formula unavailable.
    WIND_UP_SPEED_FACTOR: 1.01, // Multiplier for computed wind-up duration (calibrated to preserve current wind-up feel after STOP_SLOWDOWN_FACTOR recalibration)
    WIND_UP_DISTANCE_MULTIPLIER: 2.0, // Wind-up pull distance vs bounce overshoot (1.0 = exact match, >1.0 = stronger pull to visually match bounce)
    REEL_START_DELAY: 50, // Fallback delay between each reel starting (ms) - used when REEL_START_DELAYS not defined
    WIND_UP_CHECK_BUFFER: 80, // Safety buffer for wind-up completion check (ms) — accounts for full wind-up + release animation

    // Non-linear reel delays — dynamically generated from Settings.REEL_COUNT
    // Gaps decrease for later reels (natural cascade feel)
    REEL_START_DELAYS: generateDecreasingDelays(Settings.REEL_COUNT, 50, 7, 25), // Per-reel start delays (ms) — starts at 50ms gap, decreases by 7ms per reel, min 25ms
    REEL_STOP_DELAYS_AUTO: generateLinearDelays(Settings.REEL_COUNT, 350), // Auto stop (ms) — constant gap >= LANDING_DURATION
    REEL_STOP_DELAYS_MANUAL: generateDecreasingDelays(
      Settings.REEL_COUNT,
      50,
      -10,
      50,
    ), // Manual stop (ms) — tighter cascade, ~50-70ms gaps

    // Stop delay settings (fallbacks when REEL_STOP_DELAYS not defined)
    STOP_DELAY_MANUAL: 50, // Delay between reels for manual stop (matches REEL_START_DELAY for chronological order)
    STOP_DELAY_AUTO: 250, // Delay between reels for automatic stop (cascade effect)


    // Auto-play speed mode settings
    AUTO_PLAY_SPEEDS: {
      AUTO: {
        SPIN_DURATION: 450, // Animation loop duration for regular spinning
        SPEED_MS_PER_SYMBOL_HEIGHT: 46, // ── THE SPEED KNOB ── ms to travel one symbol height (higher = slower)
        BOUNCE_OVERSHOOT: 1.1, // Landing bounce intensity (artistic feel, not speed-dependent)
        STOP_SLOWDOWN_FACTOR: 0.693, // Landing speed vs spin speed (< 1.0 = snappier landing). Derived: 375ms / 541ms velocity-matched = 0.693
        AUTO_PLAY_DELAY: 100, // Delay between auto-play spins
        AUTO_STOP_TIMEOUT: 400, // Time before automatic stop starts
        LANDING_PEAK_OFFSET: 0.8, // Multiplier on overshoot peak timing (1.0 = exact trough, <1.0 = fires earlier)
        ACCEL_SPEED_RATIO: 4.545, // Acceleration duration = SPEED_MS × ratio (44 × 4.545 = 200ms at current speed)
        BLUR_VELOCITY_THRESHOLD: 0.6, // Fraction of max speed at which blur activates (0.6 = 60%)
        // LANDING_DURATION removed — computed at runtime from velocity formula:
        // duration = (overshoot + 3) × distance / spinVelocity × STOP_SLOWDOWN_FACTOR
      },
      QUICK: {
        SPIN_DURATION: 90, // Shorter spin duration than AUTO (400ms vs 860ms)
        SPEED_MS_PER_SYMBOL_HEIGHT: 44, // Same as AUTO
        BOUNCE_OVERSHOOT: 1.0, // Same as AUTO
        AUTO_PLAY_DELAY: 500, // Same as AUTO
        AUTO_STOP_TIMEOUT: 500, // Same as AUTO
        ACCELERATION_DURATION: 200, // Same as AUTO
        BLUR_VELOCITY_THRESHOLD: 0.6, // Same as AUTO
        // LANDING_DURATION removed — computed at runtime from velocity formula:
        // duration = (overshoot + 3) × distance / spinVelocity × STOP_SLOWDOWN_FACTOR
        STOP_SLOWDOWN_FACTOR: 0.710, // Derived: 375ms / 528ms velocity-matched = 0.710
        SKIP_WIND_UP: true, // No wind-up animation
        REEL_STOP_DELAYS: generateDecreasingDelays(
          Settings.REEL_COUNT,
          70,
          5,
          55,
        ), // Same as manual stop cascade for snappy feel
      },
      TURBO: {
        // Timing
        SPIN_DELAY: 150,
        AUTO_PLAY_DELAY: 16,
        AUTO_STOP_TIMEOUT: 16,
        MIN_SPIN_DURATION: 5, // Barely moves — almost instant snap to landing

        // Unified spin parameters (ticker-based turbo — same system as normal/quick)
        SPEED_MS_PER_SYMBOL_HEIGHT: 36, // Very fast spin — rapid blur
        ACCELERATION_DURATION: 0, // Instant full speed
        // LANDING_DURATION removed — computed at runtime from velocity formula
        BOUNCE_OVERSHOOT: 1.0, // Controlled bounce
        STOP_SLOWDOWN_FACTOR: 0.962, // Derived: 300ms / 312ms velocity-matched = 0.962
        TENSION_LANDING_SLOWDOWN: 1.0, // No extra slowdown — turbo decelerated speed is still fast enough
        BLUR_VELOCITY_THRESHOLD: 0.6,
        SKIP_WIND_UP: true, // No wind-up animation
        REEL_START_DELAY: 16,
        REEL_START_DELAYS: [0, 16, 32, 48, 64], // 1 frame (16ms) per reel
        REEL_STOP_DELAY: 16,
        REEL_STOP_DELAYS: [0, 16, 32, 48, 64], // 1 frame (16ms) per reel

      },
      SUPER_TURBO: {
        // Slightly faster than TURBO — tuned for rapid-fire gameplay
        SPIN_DELAY: 75,
        AUTO_PLAY_DELAY: 16,
        AUTO_STOP_TIMEOUT: 16,
        MIN_SPIN_DURATION: 5,

        SPEED_MS_PER_SYMBOL_HEIGHT: 14,
        ACCELERATION_DURATION: 0,
        // LANDING_DURATION removed — computed at runtime from velocity formula
        BOUNCE_OVERSHOOT: 0, // No bounce — straight drop into place
        STOP_SLOWDOWN_FACTOR: 1.020, // Derived: ~150ms landing
        TENSION_LANDING_SLOWDOWN: 1.0, // No extra slowdown — super turbo decelerated speed is still fast enough
        BLUR_VELOCITY_THRESHOLD: 0.6,
        SKIP_WIND_UP: true,
        REEL_START_DELAY: 0,
        REEL_START_DELAYS: generateLinearDelays(Settings.REEL_COUNT, 0),
        REEL_STOP_DELAY: 0,
        REEL_STOP_DELAYS: generateLinearDelays(Settings.REEL_COUNT, 0),

      },
    },
  },
  // ── Cosmetic coin generation during spin animation ──
  // Controls which symbols randomly appear while reels are spinning (visual only — landing symbols come from API).
  // Two modes:
  //   "royal_collection" — Max 1 coin per reel, coins only on COIN_REELS, royal only on ROYAL_REELS
  //   "hold_and_win"     — Multiple coins per reel allowed, coins on all reels (original H&W behavior)
  //   null               — No coins or royals generated during spin animation
  COIN_GENERATION: {
    MODE: null, // "royal_collection" | "hold_and_win" | null
    PROBABILITY: 0.2, // Chance per symbol position to become a coin (35% — tuning)
    ROYAL_PROBABILITY: 0.2, // Chance per symbol position to become a royal (middle reel only)
    COIN_REELS: GAME_LAYOUT.REEL_INDICES.filter(
      (i) => i !== GAME_LAYOUT.MIDDLE_REEL,
    ), // All reels except middle (adapts to REEL_COUNT)
    ROYAL_REELS: [GAME_LAYOUT.MIDDLE_REEL], // Middle reel only (adapts to REEL_COUNT)
    MIN_COIN_SPACING: 3, // Minimum positions between coins (3 = at least 2 non-coin symbols between coins, max 1 visible at a time on a 3-row grid)
  },
  HOLD_AND_WIN: {
    ENABLED: false, // If false: hold and win feature is completely disabled, no intro dialog will be shown
    MAX_WIN_MULTIPLIER: PROVIDER_CONFIG.MAX_WIN_MULTIPLIER, // Max win threshold multiplier — sourced from providerConfig
    INTRO_DELAY: 500, // Delay in ms before showing hold and win intro dialog after reels stop
    COIN_VALUE_FONT_SIZE_MULTIPLIER: 0.2, // Font size for coin values as percentage of symbol height
    // Spin configuration
    SPEED_MS_PER_SYMBOL_HEIGHT: 134, // Milliseconds to move one symbol height (lower = faster)
    BLURRED_TEXTURES_ENABLED: true, // If true: use blurred textures during spin, if false: use regular textures
    // Stop animation configuration
    STOP_SLOWDOWN_FACTOR: 0.33, // Stop speed relative to spin speed (lower = slower stop, 0.35 = ~3x slower than spin)
    BOUNCE_OVERSHOOT: 0.9, // Bounce effect on landing (0 = no bounce, 1.2 = medium bounce, 2.0 = strong bounce)
    // Stop timing configuration
    SPIN_DURATION: 500, // Total spin duration before automatic stop (milliseconds) - needs to be long enough for API response
    SYMBOL_STOP_BASE_DELAY: 50, // Base delay before first symbol stops in cascading (milliseconds) - gives overlay controllers time to initialize
    SYMBOL_STOP_CASCADE_DELAY: 300, // Delay between each symbol stop in cascading for automatic stops (milliseconds)
    SYMBOL_STOP_CASCADE_DELAY_MANUAL: 50, // Manual stop cascade delay for TURBO mode (milliseconds)
    SYMBOL_STOP_CASCADE_DELAY_MANUAL_NORMAL: 25, // Manual stop cascade delay for NORMAL/AUTO speed (milliseconds) - faster for better responsiveness
    // Autoplay configuration
    AUTOPLAY_FIRST_SPIN_DELAY: 800, // Delay after transition completes before first autoplay spin starts in normal mode (milliseconds)
    AUTOPLAY_FIRST_SPIN_DELAY_TURBO: 800, // Delay for turbo mode - quick start after transition (milliseconds)
    AUTOPLAY_SPIN_DELAY: 800, // Delay between autoplay spins in normal mode (milliseconds)
    AUTOPLAY_SPIN_DELAY_TURBO: 725, // Delay between autoplay spins in turbo mode
    // Symbol tension mode — per-symbol anticipation for last blank cell
    SYMBOL_TENSION: {
      ENABLED: true, // Enable symbol tension/anticipation for Hold and Win
      COIN_THRESHOLD: 19, // Trigger when this many coins are on reelset (remaining blanks = 20 - COIN_THRESHOLD)
      COIN_GENERATION_PROBABILITY: 0.3, // Probability of generating coins during tension (30% vs 12.5% normal)
      DURATION: 3500, // Duration that symbol spins in tension mode before stopping (milliseconds) - increased for more anticipation
      CASCADE_DELAY: 750, // Delay between each tension symbol stop in cascading (milliseconds)
      TURBO_DURATION: 800, // Shorter tension duration for turbo mode (milliseconds)
      TURBO_CASCADE_DELAY: 150, // Faster cascade delay for turbo tension mode (milliseconds)
    },
    // Turbo mode configuration
    TURBO: {
      SPIN_DURATION: 200, // Minimal spin duration in turbo - drop almost immediately after server response (milliseconds)
      DROP_DURATION: 500, // Drop animation duration - matches regular stop feel (milliseconds)
      BOUNCE_OVERSHOOT: 1.0, // No bounce - lands exactly in place (1.0 = no overshoot)
      SYMBOL_STOP_CASCADE_DELAY: 65, // Cascade delay between symbols in turbo mode (milliseconds)
    },
    // Collection phase configuration
    COLLECTION: {
      TRIGGER_DELAY: 1000, // Delay before starting collection after conditions are met (milliseconds)
      INTRO_ANIMATION_DELAY: 750, // Wait time for collector intro animation (milliseconds)
      SYMBOL_COLLECT_DELAY: 750, // Delay between collecting each symbol (milliseconds)
      MULTIPLIER_UPDATE_DELAY: 50, // Delay after multiplier animation completes to show the multiplied value before fading (milliseconds)
      FINISH_DELAY: 500, // Delay after all symbols collected before finishing (milliseconds)
      OUTRO_DELAY: 1000, // Delay before triggering outro after collection finishes (milliseconds)
      // Counter animation durations
      COUNTER_ANIMATION_PER_COIN: 300, // Counter animation duration per coin in normal mode (milliseconds)
      COUNTER_ANIMATION_SKIP_MODE: 2000, // Counter animation duration in skip mode (milliseconds)
      COUNTER_ANIMATION_MULTIPLIER: 1000, // Counter animation duration for progress bar multiplier (milliseconds)
      ESTIMATED_TIME_PER_COIN: 1600, // Estimated time per coin for continuous counter animation (milliseconds)
    },
  },
  // Win threshold multipliers - loaded from provider-specific configuration
  WIN_THRESHOLDS: PROVIDER_CONFIG.WIN_THRESHOLDS,
  // Win celebration animation configuration
  WIN_CELEBRATION: {
    AUTO_CLOSE_DELAY: 3000, // Auto-close delay after counter finishes (milliseconds) - popup closes automatically if user doesn't interact
  },
  // Max win dialog configuration
  MAX_WIN: {
    ENABLED: true, // When true, max_win animation plays as final tier in win sequence
    AUTO_CLOSE_DELAY: 30000, // Auto-close delay after counter finishes (30 seconds) - dialog closes automatically if user doesn't interact
    MULTIPLIER:
      PROVIDER_CONFIG.WIN_THRESHOLDS.MAX_WIN_THRESHOLD ||
      PROVIDER_CONFIG.MAX_WIN_MULTIPLIER ||
      5000, // Max win cap multiplier (bet × this value) - loaded from provider config
  },
  // Hold and win outro dialog configuration
  HOLD_AND_WIN_OUTRO: {
    AUTO_CLOSE_DELAY: 30000, // Auto-close delay after counter finishes (30 seconds) - dialog closes automatically if user doesn't interact
  },
  // Emergency landing pattern for API rejections (e.g., error 205 free rounds available)
  // Pattern is now generated dynamically via generateNonWinningCombination() utility
  EMERGENCY_LANDING: {
    // Fallback pattern used if dynamic generation fails after max attempts
    // Guaranteed non-winning combination: no coins, no wilds, all different symbols
    // Dynamically generated fallback pattern — guaranteed non-winning combination for any reel count
    FALLBACK_PATTERN: Array.from(
      { length: Settings.REEL_COUNT },
      (_, reelIdx) =>
        Array.from(
          { length: GAME_LAYOUT.SYMBOLS_VISIBLE },
          (_, symIdx) => (reelIdx + symIdx + 4) % 10 || 4,
        ),
    ),
  },
};

// Win type string constants - loaded from provider-specific configuration
export const WIN_TYPES = PROVIDER_CONFIG.WIN_TYPES;

// Animated win types - which wins trigger full-screen celebration
export const ANIMATED_WIN_TYPES = PROVIDER_CONFIG.ANIMATED_WIN_TYPES;

// Centralized mapping of win types to their thresholds (dynamically generated)
export const WIN_TYPE_THRESHOLDS = Object.keys(
  PROVIDER_CONFIG.WIN_TYPES,
).reduce((acc, key) => {
  const thresholdKey = `${key}_THRESHOLD`;
  if (PROVIDER_CONFIG.WIN_THRESHOLDS[thresholdKey]) {
    acc[PROVIDER_CONFIG.WIN_TYPES[key]] =
      PROVIDER_CONFIG.WIN_THRESHOLDS[thresholdKey];
  }
  return acc;
}, {});

// Expanding wild functions - cascading wild animations
/**
 * Build cascade sequences dynamically based on Settings.SYMBOLS_VISIBLE
 * Returns sequences for each possible wild position
 */
export const buildCascadeSequences = () => {
  const visibleCount = GAME_LAYOUT.SYMBOLS_VISIBLE;
  const sequences = {};

  for (let wildPos = 0; wildPos < visibleCount; wildPos++) {
    const sequence = [wildPos]; // Start with wild position

    if (wildPos === 0) {
      // Wild at top → cascade down
      for (let i = 1; i < visibleCount; i++) {
        sequence.push(i);
      }
    } else if (wildPos === visibleCount - 1) {
      // Wild at bottom → cascade up
      for (let i = visibleCount - 2; i >= 0; i--) {
        sequence.push(i);
      }
    } else {
      // Wild in middle → fill one direction first, then the other
      const isLowerHalf = wildPos >= Math.floor(visibleCount / 2);

      if (isLowerHalf) {
        // Fill below first, then above
        for (let i = wildPos + 1; i < visibleCount; i++) {
          sequence.push(i);
        }
        for (let i = wildPos - 1; i >= 0; i--) {
          sequence.push(i);
        }
      } else {
        // Fill above first, then below
        for (let i = wildPos - 1; i >= 0; i--) {
          sequence.push(i);
        }
        for (let i = wildPos + 1; i < visibleCount; i++) {
          sequence.push(i);
        }
      }
    }

    sequences[wildPos] = sequence;
  }

  return sequences;
};

export const EXPANDING_WILD_SEQUENCES = buildCascadeSequences();

/**
 * Detect which middle reels (1, 2, 3) have wild symbols when there's a win
 * Wild should expand if there's a connected winning line adjacent to it
 * @param {Object} currentRoundRef - Ref containing current round data
 * @param {Array} wins - Array of winning combinations from currentRound.wins
 * @returns {Array<{reelIndex: number, wildPosition: number}>} Array of objects with reel index and wild position
 */
export const detectExpandingWilds = (currentRoundRef, wins) => {
  const expandedReels = currentRoundRef.current?.reels;

  if (!expandedReels || !wins || !Array.isArray(wins) || wins.length === 0) {
    return [];
  }

  // Use wild symbol ID from settings (calculated once at module load)
  if (WILD_SYMBOL_ID === null) {
    return [];
  }

  // Dynamic middle reel indices — all reels except first and last
  const middleReelIndices = Array.from(
    { length: Math.max(0, Settings.REEL_COUNT - 2) },
    (_, i) => i + 1,
  );
  const reelsWithWilds = [];

  for (const reelIndex of middleReelIndices) {
    const visibleSymbols = expandedReels[reelIndex];
    if (!visibleSymbols || !Array.isArray(visibleSymbols)) {
      continue;
    }

    // Find FIRST wild position in visible symbols (searching for w1 by symbol ID)
    const wildPosition = visibleSymbols.findIndex(
      (symbolId) => symbolId === WILD_SYMBOL_ID,
    );

    // If wild found, check if it's part of a connected winning line
    if (wildPosition !== -1) {
      // Check if ANY winning combination has this wild reel as part of a connected line
      const isPartOfConnectedWin = wins.some((win) => {
        if (!Array.isArray(win.positions) || win.positions.length === 0) {
          return false;
        }

        // Get all reels involved in this win
        const reelsInWin = win.positions.map(([r]) => r);
        const uniqueReels = [...new Set(reelsInWin)].sort((a, b) => a - b);

        // Check if wild reel is in this win
        if (!uniqueReels.includes(reelIndex)) {
          return false;
        }

        // Find the rightmost connected reel (consecutive from left)
        let rightmostConnected = -1;
        for (let i = 0; i < uniqueReels.length; i++) {
          const currentReel = uniqueReels[i];

          // First reel must be 0 or 1 (wins start from left)
          if (i === 0 && currentReel > 1) {
            break;
          }

          // Check if this reel is consecutive to the previous one
          if (i > 0 && currentReel !== uniqueReels[i - 1] + 1) {
            break; // Gap found, stop here
          }

          rightmostConnected = currentReel;
        }

        // Wild should expand if it's part of the connected sequence (not after a gap)
        const isInConnectedSequence = rightmostConnected >= reelIndex;

        return isInConnectedSequence;
      });

      // Expand wild if it's part of a connected winning line
      if (isPartOfConnectedWin) {
        reelsWithWilds.push({
          reelIndex,
          wildPosition, // This is overlay position (0-3)
        });
      }
    }
  }

  return reelsWithWilds;
};

/**
 * Get the appear animation duration for expanding wilds
 * @param {Object} overlayReelController - Controller to get animation duration
 * @returns {number} Appear animation duration in milliseconds (default: 1000ms)
 */
export const getAppearAnimationDuration = (overlayReelController) => {
  // Default fallback: 1000ms
  let appearAnimationDuration = 1000;

  if (overlayReelController) {
    // Use wild symbol ID from settings (calculated once at module load)
    if (WILD_SYMBOL_ID === null) {
      return appearAnimationDuration;
    }

    // Try to get duration from a spine instance
    const spineKey = `0_${WILD_SYMBOL_ID}`; // First position, w1 wild symbol
    const spineInstance =
      overlayReelController.prePositionedSpines?.get(spineKey);

    if (spineInstance?.skeleton?.data?.animations) {
      // Try "appear" first, then "land" as fallback
      let targetAnimation = spineInstance.skeleton.data.animations.find(
        (anim) => anim.name.toLowerCase().includes("appear"),
      );
      if (!targetAnimation) {
        targetAnimation = spineInstance.skeleton.data.animations.find((anim) =>
          anim.name.toLowerCase().includes("land"),
        );
      }
      if (targetAnimation?.duration) {
        appearAnimationDuration = Math.round(targetAnimation.duration * 1000);
      }
    }
  }

  return appearAnimationDuration;
};

/**
 * Calculate the total duration for cascading expanding wild animation
 * @param {number} wildPosition - Position where wild was detected (0-3)
 * @param {Object} overlayReelController - Controller to get animation duration
 * @returns {number} Total duration in milliseconds
 */
export const calculateCascadeDuration = (
  wildPosition,
  overlayReelController,
) => {
  // Get the sequence for this wild position
  const sequence =
    EXPANDING_WILD_SEQUENCES[wildPosition] ||
    Array.from({ length: GAME_LAYOUT.SYMBOLS_VISIBLE }, (_, i) => i);

  // Get appear animation duration using helper function
  const appearAnimationDuration = getAppearAnimationDuration(
    overlayReelController,
  );

  // Calculate total cascade time (event-aware timing):
  // - Dynamic delays between symbols: (sequence.length - 1) * (appearDuration * overlap%)
  // - Plus appear animation duration for the last symbol
  const cascadeDelay = Math.round(
    appearAnimationDuration *
    FEATURES_CONFIG.EXPANDING_WILDS.CASCADE_OVERLAP_PERCENTAGE,
  );
  const cascadeDelayTime = (sequence.length - 1) * cascadeDelay;
  const totalDuration = cascadeDelayTime + appearAnimationDuration;

  return totalDuration;
};

/**
 * Play cascading expanding wild animation for a single reel
 * @param {number} reelIndex - Index of the reel to animate
 * @param {number} wildPosition - Position where wild was detected (0-3)
 * @param {Function} dispatch - Redux dispatch function to update state
 */
export const playCascadingExpandingWild = async (
  reelIndex,
  wildPosition,
  reelRefs,
  overlayReelRefs,
  currentTimeoutsRef,
  dispatch,
) => {
  const reels = reelRefs.current;
  const overlayReels = overlayReelRefs.current;

  if (!reels || !overlayReels) {
    return;
  }

  const reelRef = reels[reelIndex];
  const overlayReelRef = overlayReels[reelIndex];

  if (!reelRef || !overlayReelRef) {
    return;
  }

  const reelController = reelRef?.getReelController?.();
  const overlayReelController = overlayReelRef?.getOverlayReelController?.();

  if (!reelController || !overlayReelController) {
    return;
  }

  // Use wild symbol ID from settings (calculated once at module load)
  if (WILD_SYMBOL_ID === null) {
    return;
  }

  // Get the sequence for this wild position
  const sequence =
    EXPANDING_WILD_SEQUENCES[wildPosition] ||
    Array.from({ length: GAME_LAYOUT.SYMBOLS_VISIBLE }, (_, i) => i);

  // Track all symbol replacements for Redux update
  const symbolReplacements = [];

  // Track the last spine instance for event listening
  let lastSpineInstance = null;

  // Calculate cascade delay dynamically based on animation duration (event-aware timing)
  const appearAnimationDuration = getAppearAnimationDuration(
    overlayReelController,
  );
  const cascadeDelay = Math.round(
    appearAnimationDuration *
    FEATURES_CONFIG.EXPANDING_WILDS.CASCADE_OVERLAP_PERCENTAGE,
  );

  // Play animations in sequence with dynamic delays
  for (let i = 0; i < sequence.length; i++) {
    const symbolIndex = sequence[i];

    // Show overlay appear animation at this position (using w1 wild symbol)
    overlayReelController.showAppearAnimationAtPosition(
      symbolIndex,
      WILD_SYMBOL_ID,
    );

    // Update reel texture immediately (no RAF needed in async function)
    reelController.replaceSymbolAtPosition(symbolIndex, WILD_SYMBOL_ID);

    // Track this replacement for Redux update
    symbolReplacements.push({
      position: symbolIndex,
      symbolId: WILD_SYMBOL_ID,
    });

    // Get the spine instance for the last symbol (for event listening)
    if (i === sequence.length - 1) {
      const spineKey = `${symbolIndex}_${WILD_SYMBOL_ID}`;
      lastSpineInstance =
        overlayReelController.prePositionedSpines?.get(spineKey);
    }

    // Wait before next symbol (except for the last one) - event-aware timing
    // IMPORTANT: Do NOT push to currentTimeoutsRef — these are cascade-local delays.
    // Pushing to the shared ref causes reel N's timeout to be cleared when reel M
    // dispatches updateReelSymbols (triggering a re-render that clears the ref).
    if (i < sequence.length - 1) {
      await new Promise((resolve) => {
        const { default: TickerTimeout } = require("./utils/TickerTimeout");
        new TickerTimeout(resolve, cascadeDelay);
      });
    }
  }

  // Update Redux state with all wild replacements for this reel
  // This ensures next spin reads correct symbols from currentRound.reels
  if (dispatch && symbolReplacements.length > 0) {
    // Import the action dynamically to avoid circular dependency
    const { updateReelSymbols } = await import("./State/actions");
    dispatch(updateReelSymbols({ reelIndex, symbolReplacements }));
  }

  // Wait for the last symbol's animation to complete (event-driven)
  // Resolves on any non-looping animation complete — works for both "appear" and "land" fallback
  if (lastSpineInstance && lastSpineInstance.state) {
    return new Promise((resolve) => {
      const completeListener = {
        complete: () => {
          lastSpineInstance.state.removeListener(completeListener);
          resolve();
        },
      };
      lastSpineInstance.state.addListener(completeListener);
    });
  }
};

/**
 * Get the "in" animation duration for expanding wild full-reel animation
 * @param {Object} overlayReelController - Controller to get animation duration
 * @returns {number} "in" animation duration in milliseconds (default: 300ms)
 */
export const getExpandingWildInAnimationDuration = (overlayReelController) => {
  // Default fallback: 300ms (typical "in" animation duration)
  let inAnimationDuration = 300;

  if (overlayReelController?.expandingWildSpine?.skeleton?.data?.animations) {
    const inAnimation =
      overlayReelController.expandingWildSpine.skeleton.data.animations.find(
        (anim) => anim.name === "in",
      );
    if (inAnimation?.duration) {
      inAnimationDuration = Math.round(inAnimation.duration * 1000);
    }
  }

  return inAnimationDuration;
};

/**
 * Play full-reel expanding wild animation for a single reel
 * @param {number} reelIndex - Index of the reel to animate
 * @param {Object} reelRefs - Refs to reel components (for texture replacement)
 * @param {Object} overlayReelRefs - Refs to overlay reel components
 * @returns {Promise} Resolves when "appear" animation completes (event-driven)
 */
export const playFullReelExpandingWild = async (
  reelIndex,
  reelRefs,
  overlayReelRefs,
) => {
  const reels = reelRefs.current;
  const overlayReels = overlayReelRefs.current;

  if (!overlayReels || !reels) {
    return;
  }

  const reelRef = reels[reelIndex];
  const overlayReelRef = overlayReels[reelIndex];

  if (!overlayReelRef || !reelRef) {
    return;
  }

  // Get controllers
  const overlayReelController = overlayReelRef?.getOverlayReelController?.();

  if (!overlayReelController) {
    return;
  }

  // Play the full-reel animation (appear → idle)
  // This will initialize the spine if needed
  overlayReelRef.playExpandingWildFullReel?.();

  // Get the expanding wild spine instance
  const expandingWildSpine = overlayReelController.getExpandingWildSpine?.();

  if (!expandingWildSpine || !expandingWildSpine.state) {
    return;
  }

  // Wait for "appear" animation to complete (event-driven)
  return new Promise((resolve) => {
    const completeListener = {
      complete: (entry) => {
        if (entry.animation.name === "appear") {
          expandingWildSpine.state.removeListener(completeListener);
          resolve();
        }
      },
    };
    expandingWildSpine.state.addListener(completeListener);
  });
};
